import { solicitar } from '../identidad/cliente-identidad.js'

export type EstadoCatalogo = 'ACTIVO' | 'INACTIVO'
export interface Programa {
  id: string
  codigo: string
  nombre: string
  estado: EstadoCatalogo
  version: number
}
export interface Periodo {
  id: string
  codigo: string
  fechaInicio: string
  fechaFin: string
  estado: 'ACTIVO' | 'CERRADO'
  version: number
}
export interface Institucion {
  id: string
  nombre: string
  nit: string
  correo: string
  celular: string
  direccion: string
  estado: EstadoCatalogo
  version: number
}
export interface Sede {
  id: string
  institucionId: string
  institucionNombre?: string
  nombre: string
  direccion: string
  celular: string
  estado: EstadoCatalogo
  version: number
}
export interface Jornada {
  id: string
  sedeId: string
  sedeNombre?: string
  nombre: 'MANANA' | 'TARDE'
  estado: EstadoCatalogo
  version: number
}
export interface Pagina<T> {
  datos: T[]
  siguienteCursor: string | null
}

const listar = <T>(ruta: string): Promise<Pagina<T>> => solicitar(`${ruta}?limite=100`)
const crear = <T>(ruta: string, entrada: object): Promise<T> =>
  solicitar(ruta, { method: 'POST', body: JSON.stringify(entrada) })
const actualizar = <T>(ruta: string, entrada: object): Promise<T> =>
  solicitar(ruta, { method: 'PATCH', body: JSON.stringify(entrada) })

export const listarProgramas = (): Promise<Pagina<Programa>> => listar('/api/v1/programas')
export const crearPrograma = (entrada: { codigo: string; nombre: string }): Promise<Programa> =>
  crear('/api/v1/programas', entrada)
export const actualizarPrograma = (
  dato: Programa,
  estadoOrDatos: EstadoCatalogo | { codigo?: string; nombre?: string },
  datos?: { codigo?: string; nombre?: string },
): Promise<Programa> =>
  actualizar(`/api/v1/programas/${dato.id}`, {
    version: dato.version,
    ...(typeof estadoOrDatos === 'string' ? { estado: estadoOrDatos, ...datos } : estadoOrDatos),
  })
export const listarPeriodos = (): Promise<Pagina<Periodo>> => listar('/api/v1/periodos')
export const crearPeriodo = (entrada: {
  codigo: string
  fechaInicio: string
  fechaFin: string
}): Promise<Periodo> => crear('/api/v1/periodos', entrada)
export const actualizarPeriodo = (
  dato: Periodo,
  entrada: { codigo?: string; fechaInicio?: string; fechaFin?: string },
): Promise<Periodo> =>
  actualizar(`/api/v1/periodos/${dato.id}`, { version: dato.version, ...entrada })
export const cerrarPeriodo = (dato: Periodo): Promise<Periodo> =>
  crear(`/api/v1/periodos/${dato.id}/cierre`, { version: dato.version })
export const listarInstituciones = (): Promise<Pagina<Institucion>> =>
  listar('/api/v1/instituciones')
export const crearInstitucion = (
  entrada: Omit<Institucion, 'id' | 'estado' | 'version'>,
): Promise<Institucion> => crear('/api/v1/instituciones', entrada)
export const actualizarInstitucion = (
  dato: Institucion,
  entrada: EstadoCatalogo | Partial<Omit<Institucion, 'id' | 'estado' | 'version'>>,
): Promise<Institucion> =>
  actualizar(`/api/v1/instituciones/${dato.id}`, {
    version: dato.version,
    ...(typeof entrada === 'string' ? { estado: entrada } : entrada),
  })
export const listarSedes = (): Promise<Pagina<Sede>> => listar('/api/v1/sedes')
export const crearSede = (entrada: {
  institucionId: string
  nombre: string
  direccion: string
  celular: string
}): Promise<Sede> => crear('/api/v1/sedes', entrada)
export const actualizarSede = (dato: Sede, estado: EstadoCatalogo): Promise<Sede> =>
  actualizar(`/api/v1/sedes/${dato.id}`, { version: dato.version, estado })
export const actualizarSedeDatos = (
  dato: Sede,
  entrada: { nombre?: string; direccion?: string; celular?: string },
): Promise<Sede> => actualizar(`/api/v1/sedes/${dato.id}`, { version: dato.version, ...entrada })
export const listarJornadas = (): Promise<Pagina<Jornada>> => listar('/api/v1/jornadas')
export const crearJornada = (entrada: {
  sedeId: string
  nombre: 'MANANA' | 'TARDE'
}): Promise<Jornada> => crear('/api/v1/jornadas', entrada)
export const actualizarJornada = (
  dato: Jornada,
  entrada: EstadoCatalogo | { nombre?: 'MANANA' | 'TARDE' },
): Promise<Jornada> =>
  actualizar(`/api/v1/jornadas/${dato.id}`, {
    version: dato.version,
    ...(typeof entrada === 'string' ? { estado: entrada } : entrada),
  })
